// Players tab — rival count + AI difficulty, and the local seat's alignment
// and assists. Split out of CustomizeTabs.tsx.

import type { AiProfile, Assists } from '@aod/protocol';
import { cn } from '@aod/ui/lib/utils';
import { type MessageKey, useT } from '../../i18n/index.js';
import type { TabProps } from '../CustomizeTabs.js';
import { SegmentedRow, StepperRow, SwitchRow } from '../controls.js';
import {
  type SetupDraft,
  setAiProfile,
  setAlignment,
  setAssist,
  setTuning,
} from '../setupModel.js';

// ── Players ────────────────────────────────────────────────────────────

const AI_PROFILES: readonly AiProfile[] = ['gentle', 'easy', 'normal', 'hard', 'brutal'];

export function PlayersTab({ draft, onChange }: TabProps) {
  const t = useT();
  const a = draft.humanAssists;
  const assist = <K extends keyof Assists>(key: K, value: Assists[K]) =>
    onChange(setAssist(draft, key, value));
  const opts = <K extends 'handicap' | 'autoManageCities' | 'hints' | 'pauseOnDanger' | 'uiMode'>(
    key: K,
    values: readonly Assists[K][],
  ) => values.map((v) => ({ value: v, label: t(`setup.assist.${key}.${v}` as MessageKey) }));
  const heading = 'text-xs font-bold uppercase tracking-wider text-muted-foreground';
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section aria-labelledby="setup-players-rivals" className="flex flex-col gap-5">
        <h3 id="setup-players-rivals" className={heading}>
          {t('setup.players.rivals')}
        </h3>
        <StepperRow
          label={t('setup.players.opponents')}
          value={draft.ruleset.tuning.aiOpponents}
          min={1}
          max={7}
          step={1}
          format={(v) => String(v)}
          onChange={(v) => onChange(setTuning(draft, 'aiOpponents', v))}
          decreaseLabel={t('setup.decrease', { label: t('setup.players.opponents') })}
          increaseLabel={t('setup.increase', { label: t('setup.players.opponents') })}
        />
        <SegmentedRow
          label={t('setup.players.profile')}
          value={draft.aiProfile}
          options={AI_PROFILES.map((p) => ({ value: p, label: t(`setup.players.profile.${p}`) }))}
          onChange={(v) => onChange(setAiProfile(draft, v))}
        />
      </section>
      <section aria-labelledby="setup-players-you" className="flex flex-col gap-5">
        <div>
          <h3 id="setup-players-you" className={cn(heading, 'mb-0.5')}>
            {t('setup.players.you')}
          </h3>
          <p className="text-xs text-muted-foreground">{t('setup.players.youHint')}</p>
        </div>
        <SegmentedRow
          label={t('setup.players.alignment')}
          description={t('setup.players.alignment.desc')}
          value={draft.alignment ?? 'none'}
          options={(['none', 'light', 'dark'] as const).map((v) => ({
            value: v,
            label: t(`setup.players.alignment.${v}` as MessageKey),
          }))}
          onChange={(v) => onChange(setAlignment(draft, v === 'none' ? null : v))}
        />
        <SegmentedRow
          label={t('setup.assist.hints')}
          value={a.hints}
          options={opts('hints', ['off', 'smart', 'always'])}
          onChange={(v) => assist('hints', v)}
        />
        <SegmentedRow
          label={t('setup.assist.pauseOnDanger')}
          value={a.pauseOnDanger}
          options={opts('pauseOnDanger', ['off', 'major', 'all'])}
          onChange={(v) => assist('pauseOnDanger', v)}
        />
        <SegmentedRow
          label={t('setup.assist.speedCap')}
          value={a.speedCap}
          options={([1, 2, 4] as const).map((v) => ({ value: v, label: `${v}×` }))}
          onChange={(v) => assist('speedCap', v)}
        />
        <SegmentedRow
          label={t('setup.assist.handicap')}
          value={a.handicap}
          options={opts('handicap', ['none', 'easy', 'very_easy'])}
          onChange={(v) => assist('handicap', v)}
        />
        <SegmentedRow
          label={t('setup.assist.autoManageCities')}
          value={a.autoManageCities}
          options={opts('autoManageCities', ['off', 'suggest', 'auto'])}
          onChange={(v) => assist('autoManageCities', v)}
        />
        <SegmentedRow
          label={t('setup.assist.uiMode')}
          value={a.uiMode}
          options={opts('uiMode', ['icons', 'standard', 'expert'])}
          onChange={(v) => assist('uiMode', v)}
        />
        <SwitchRow
          label={t('setup.assist.revealMap')}
          checked={a.revealMap}
          onChange={(v) => assist('revealMap', v)}
        />
      </section>
    </div>
  );
}
